
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMigrationStore } from '@/store/migrationStore';

export interface Project {
  id: string;
  name: string;
  description?: string;
  client_name?: string;
  address?: string;
  coordinates_lat?: number;
  coordinates_lng?: number;
  status: 'active' | 'completed' | 'on-hold';
  estimated_hours?: number;
  hourly_rate?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Type helper pour les données venant de Supabase
interface SupabaseProject {
  id: string;
  name: string;
  description: string | null;
  client_name: string | null;
  address: string | null;
  coordinates_lat: number | null;
  coordinates_lng: number | null;
  status: string | null;
  estimated_hours: number | null;
  hourly_rate: number | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isActive, source } = useMigrationStore();

  useEffect(() => {
    fetchProjects();
  }, [isActive, source]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      // Déterminer quelle table utiliser selon le mode migration
      const tableName = isActive && source === 'staging' ? 'projects_staging' : 'projects';
      
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      
      // Transform the data to match the expected format
      const transformedProjects = (data || []).map((project: SupabaseProject) => ({
        id: project.id,
        name: project.name,
        description: project.description || undefined,
        client_name: project.client_name || undefined,
        address: project.address || undefined,
        coordinates_lat: project.coordinates_lat || undefined,
        coordinates_lng: project.coordinates_lng || undefined,
        status: (project.status || 'active') as 'active' | 'completed' | 'on-hold',
        estimated_hours: project.estimated_hours || undefined,
        hourly_rate: project.hourly_rate || undefined,
        is_active: project.is_active ?? true,
        created_at: project.created_at || '',
        updated_at: project.updated_at || '',
        // Legacy compatibility fields for existing components
        coordinates: project.coordinates_lat && project.coordinates_lng ? {
          lat: project.coordinates_lat,
          lng: project.coordinates_lng,
        } : { lat: 0, lng: 0 },
        client: project.client_name || '',
        estimatedHours: project.estimated_hours || 0,
        hourlyRate: project.hourly_rate || 0,
      }));

      setProjects(transformedProjects);
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les projets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { projects, loading, refetch: fetchProjects };
};
